import type { RollupRow } from './supplierRollup'
import { formatExclusivePercents } from './MetricModeToggle'
import type { RiskFilter } from './esPortfolioFilters'

export type BandCounts = {
  high: number
  medium: number
  low: number
  unscored: number
  total: number
}

const BAND_SEGMENTS = [
  { key: 'high' as const, label: 'High', className: 'bg-es-riskHigh' },
  { key: 'medium' as const, label: 'Medium', className: 'bg-es-riskMedium' },
  { key: 'low' as const, label: 'Low', className: 'bg-es-riskLow' },
  { key: 'unscored' as const, label: 'Unscored', className: 'bg-es-muted/50' },
]

/** Stacked band distribution — no chart library. */
export function RiskBandChart({
  bands,
  title = 'Risk distribution',
  onBandClick,
  formatValue,
  noun = 'suppliers',
  embedded = false,
}: {
  bands: BandCounts
  title?: string
  onBandClick?: (band: Exclude<RiskFilter, 'all'>) => void
  formatValue?: (n: number) => string
  noun?: string
  /** Drop the card chrome so the bar can sit inside another card. */
  embedded?: boolean
}) {
  const total = bands.total || bands.high + bands.medium + bands.low + bands.unscored
  const exclusivePct = formatExclusivePercents([
    bands.high,
    bands.medium,
    bands.low,
    bands.unscored,
  ])
  const Wrapper = embedded ? 'div' : 'section'
  return (
    <Wrapper
      className={
        embedded
          ? ''
          : 'bg-es-white border border-es-soft shadow-[var(--shadow-card)] rounded-[var(--radius-lg)] p-4'
      }
    >
      <h2
        className={
          embedded
            ? 'text-es-navy text-lg font-semibold mb-3'
            : 'text-sm font-bold text-es-navy uppercase tracking-wider mb-3'
        }
      >
        {title}
      </h2>
      {total <= 0 ? (
        <p className="text-sm text-es-muted">No suppliers to chart.</p>
      ) : (
        <>
          <div
            className="flex h-3 w-full overflow-hidden rounded-full bg-es-haze"
            role="img"
            aria-label={`Risk distribution of ${total} ${noun}: ${bands.high} high, ${bands.medium} medium, ${bands.low} low, ${bands.unscored} unscored`}
          >
            {BAND_SEGMENTS.map((seg, i) => {
              const n = bands[seg.key]
              if (n <= 0) return null
              const pctNum = Number(exclusivePct[i].replace('%', ''))
              return (
                <div
                  key={seg.key}
                  className={`${seg.className} h-full min-w-[2px] ${onBandClick ? 'cursor-pointer' : ''}`}
                  style={{ width: `${pctNum}%` }}
                  title={`${seg.label}: ${n} (${exclusivePct[i]})${onBandClick ? ' — click to filter' : ''}`}
                  onClick={onBandClick ? () => onBandClick(seg.key) : undefined}
                  onKeyDown={
                    onBandClick
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            onBandClick(seg.key)
                          }
                        }
                      : undefined
                  }
                  role={onBandClick ? 'button' : undefined}
                  tabIndex={onBandClick ? 0 : undefined}
                />
              )
            })}
          </div>
          <ul className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
            {BAND_SEGMENTS.map((seg, i) => {
              const n = bands[seg.key]
              const clickable = !!onBandClick
              return (
                <li key={seg.key}>
                  <button
                    type="button"
                    disabled={!clickable}
                    onClick={clickable ? () => onBandClick(seg.key) : undefined}
                    className={`flex items-center gap-2 text-xs text-es-body text-left w-full rounded-[var(--radius-sm)] ${
                      clickable
                        ? 'hover:bg-es-haze/80 cursor-pointer focus:outline-none focus:ring-1 focus:ring-es-signal'
                        : 'cursor-default'
                    }`}
                    title={clickable ? `Filter to ${seg.label} risk` : undefined}
                  >
                    <span
                      className={`inline-block w-2.5 h-2.5 rounded-sm shrink-0 ${seg.className}`}
                      aria-hidden
                    />
                    <span className="min-w-0">
                      <span className="font-semibold text-es-navy">{seg.label}</span>
                      <span className="text-es-muted tabular-nums">
                        {' '}
                        · {formatValue ? formatValue(n) : n} ({exclusivePct[i]})
                      </span>
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </>
      )}
    </Wrapper>
  )
}

/** Horizontal bars for top rollup groups (by high, then count). */
export function RollupBarsChart({
  title,
  rows,
  valueKey = 'high',
  maxBars = 6,
  emptyLabel = 'No groups to chart.',
  /** When set, show share of this total next to the count (matches table %). */
  portfolioTotal,
  onRowClick,
  formatValue,
  shareSuffix = 'of set',
}: {
  title: string
  rows: RollupRow[]
  valueKey?: 'high' | 'count'
  maxBars?: number
  emptyLabel?: string
  portfolioTotal?: number
  onRowClick?: (key: string) => void
  formatValue?: (n: number) => string
  shareSuffix?: string
}) {
  const ranked = [...rows]
    .filter((r) => r.key !== 'Unclassified')
    .sort((a, b) => b[valueKey] - a[valueKey] || b.count - a.count || a.key.localeCompare(b.key))
    .slice(0, maxBars)
  const max = Math.max(1, ...ranked.map((r) => r[valueKey]))
  const valueLabel = valueKey === 'high' ? 'High' : 'Count'
  const denom = portfolioTotal && portfolioTotal > 0 ? portfolioTotal : 0

  return (
    <section className="bg-es-white border border-es-soft shadow-[var(--shadow-card)] rounded-[var(--radius-lg)] p-4">
      <h2 className="text-sm font-bold text-es-navy uppercase tracking-wider mb-3">{title}</h2>
      {ranked.length === 0 || ranked.every((r) => r[valueKey] === 0) ? (
        <p className="text-sm text-es-muted">{emptyLabel}</p>
      ) : (
        <ul className="space-y-2.5">
          {ranked.map((r) => {
            const v = r[valueKey]
            const pctBar = Math.max(2, (v / max) * 100)
            const share =
              denom > 0 && valueKey === 'count'
                ? Math.round((r.count / denom) * 100)
                : denom > 0 && valueKey === 'high' && r.count > 0
                  ? Math.round((r.high / r.count) * 100)
                  : null
            const body = (
              <>
                <div className="flex items-baseline justify-between gap-2 text-xs mb-1">
                  <span className="font-medium text-es-navy truncate" title={r.key}>
                    {r.key}
                  </span>
                  <span className="tabular-nums text-es-muted shrink-0">
                    {formatValue ? formatValue(v) : `${v} ${valueLabel.toLowerCase()}`}
                    {!formatValue && valueKey === 'high' && r.count !== v ? ` / ${r.count}` : ''}
                    {share != null
                      ? valueKey === 'count'
                        ? ` · ${share}% ${shareSuffix}`
                        : ` · ${share}% high`
                      : ''}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-es-haze overflow-hidden">
                  <div
                    className={`h-full rounded-full ${
                      valueKey === 'high' ? 'bg-es-riskHigh' : 'bg-es-signal'
                    }`}
                    style={{ width: `${pctBar}%` }}
                  />
                </div>
              </>
            )
            return (
              <li key={r.key}>
                {onRowClick ? (
                  <button
                    type="button"
                    className="w-full text-left rounded-[var(--radius-sm)] hover:bg-es-haze/60 focus:outline-none focus:ring-1 focus:ring-es-signal p-0.5 -m-0.5"
                    onClick={() => onRowClick(r.key)}
                    title={`Filter to ${r.key}`}
                  >
                    {body}
                  </button>
                ) : (
                  body
                )}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
